import { useState } from 'react'
import { ChevronDown, ChevronRight, Info, Plus } from 'lucide-react'
import { EmptyState, ErrorState, FormDialog, PageHeader, EmployeePicker } from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select } from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import { useDepartments, useDepartmentTree, useOrgMasterMutations } from '../api'
import type { DepartmentNode } from '../types'

/** The department tree: create, rename, move, deactivate. Never delete. */
export function DepartmentsPage() {
  useBreadcrumb()
  const tree = useDepartmentTree()
  const flat = useDepartments()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const { createDepartment, updateDepartment, deactivateDepartment } = useOrgMasterMutations()

  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set())
  const [draft, setDraft] = useState<{
    id?: string; code: string; name: string; parentId: string; headEmployeeId: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dialogError, setDialogError] = useState<string | null>(null)

  const act = (work: () => Promise<unknown>, fallback: string, inDialog = false) => {
    void (async () => {
      setError(null)
      setDialogError(null)
      try {
        await work()
      } catch (caught) {
        const message = toApiProblem(caught)?.detail ?? fallback
        if (inDialog) setDialogError(message)
        else setError(message)
      }
    })()
  }

  function row(node: DepartmentNode, depth: number) {
    const isOpen = !collapsed.has(node.department.id)
    return (
      <li key={node.department.id}>
        <div
          className="flex items-center gap-2 border-b border-slate-100 py-2 text-sm"
          style={{ paddingInlineStart: `${depth * 1.25}rem` }}
        >
          {node.children.length > 0 ? (
            <button
              type="button"
              aria-expanded={isOpen}
              aria-label={isOpen ? `Collapse ${node.department.name}` : `Expand ${node.department.name}`}
              className="grid size-5 place-items-center text-slate-500"
              onClick={() => setCollapsed((current) => {
                const next = new Set(current)
                if (next.has(node.department.id)) next.delete(node.department.id)
                else next.add(node.department.id)
                return next
              })}
            >
              {isOpen
                ? <ChevronDown className="size-4" aria-hidden="true" />
                : <ChevronRight className="size-4" aria-hidden="true" />}
            </button>
          ) : (
            <span className="size-5" />
          )}
          <span className="font-mono text-xs text-slate-500">{node.department.code}</span>
          <span className={node.department.active ? '' : 'text-slate-400 line-through'}>
            {node.department.name}
          </span>
          {node.department.headEmployeeName && (
            <span className="text-xs text-slate-500">led by {node.department.headEmployeeName}</span>
          )}
          <span className="ms-auto text-xs text-slate-600">
            {node.department.headcount} person(s)
          </span>
          <Button
            type="button"
            variant="outline"
            onClick={() => {
              setError(null)
              setDraft({
                id: node.department.id,
                code: node.department.code,
                name: node.department.name,
                parentId: node.department.parentId ?? '',
                headEmployeeId: node.department.headEmployeeId ?? '',
              })
            }}
          >
            Edit
          </Button>
          {node.department.active && (
            <Button
              type="button"
              variant="outline"
              onClick={() => act(
                () => deactivateDepartment.mutateAsync(node.department.id),
                'That department could not be deactivated')}
            >
              Deactivate
            </Button>
          )}
        </div>
        {isOpen && node.children.length > 0 && (
          <ul>{node.children.map((child) => row(child, depth + 1))}</ul>
        )}
      </li>
    )
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Departments"
        subtitle="The tree everything else groups by."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({ code: '', name: '', parentId: '', headEmployeeId: '' })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New department
          </Button>
        }
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <p className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Department codes are unique across the company, not per legal entity — departments
        cross entities in a group, and headcount reports group by department across all of
        them. There is no delete: a department is deactivated, so two years of assignments
        still resolve.
      </p>

      <Card>
        {tree.isPending && <Skeleton className="m-4 h-40" />}
        {tree.isError && <ErrorState error={tree.error} onRetry={() => void tree.refetch()} />}
        {tree.isSuccess && tree.data.length === 0 && (
          <EmptyState
            title="No department exists"
            description="Everything from headcount to payroll cost groups by department, so this is usually the first thing set up."
          />
        )}
        {tree.isSuccess && tree.data.length > 0 && (
          <ul className="p-2">{tree.data.map((node) => row(node, 0))}</ul>
        )}
      </Card>

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setDialogError(null) } }}
        title={draft?.id ? 'Edit department' : 'New department'}
        submitLabel="Save"
        loading={createDepartment.isPending || updateDepartment.isPending}
        error={dialogError}
        onSubmit={() => {
          if (!draft) return
          const body = {
            legalEntityId: entities.data?.[0]?.id ?? null,
            code: draft.code,
            name: draft.name,
            parentId: draft.parentId || null,
            headEmployeeId: draft.headEmployeeId || null,
            sortOrder: 100,
            active: true,
          }
          act(async () => {
            if (draft.id) {
              await updateDepartment.mutateAsync({ id: draft.id, ...body })
            } else {
              await createDepartment.mutateAsync(body)
            }
            setDraft(null)
          }, 'That department could not be saved', true)
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div>
              <Label htmlFor="department-code">Code</Label>
              <Input
                id="department-code" value={draft.code}
                onChange={(event) => setDraft({ ...draft, code: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="department-name">Name</Label>
              <Input
                id="department-name" value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="department-parent">Sits under</Label>
              <Select
                id="department-parent"
                className="w-full"
                value={draft.parentId}
                onChange={(event) => setDraft({ ...draft, parentId: event.target.value })}
              >
                <option value="">Nothing — it is top level</option>
                {flat.data?.filter((row2) => row2.id !== draft.id).map((row2) => (
                  <option key={row2.id} value={row2.id}>{row2.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="department-head">Led by</Label>
              <EmployeePicker
                  id="department-head"
                  value={draft.headEmployeeId}
                  onChange={(employeeId) => setDraft({ ...draft, headEmployeeId: employeeId ?? '' })}
                />
              <p className="mt-1 text-xs text-slate-600">
                Resolves approvals routed to the department head.
              </p>
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
